import React from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import {
  FileText,
  Eye,
  Heart,
  Target,
  Filter,
  SortAsc,
  SortDesc,
  ExternalLink,
  Loader2,
  BarChart3,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  ThumbsUp,
  MessageSquare,
  Share2,
  MousePointer,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Line,
} from 'recharts';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { PLATFORM_COLORS, formatNumber } from './constants';

const PostsTab = ({
  postsPerformance,
  postsLoading,
  postsSortBy,
  setPostsSortBy,
  postsSortOrder,
  toggleSortOrder,
  selectedPost,
  fetchPostDetail,
  postDetail,
  postDetailLoading,
}) => (
  <TabsContent value="posts" className="space-y-6" data-testid="posts-performance-tab">
    {/* Summary Cards */}
    {postsPerformance?.summary && (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{postsPerformance.summary.total_posts}</p>
                <p className="text-sm text-slate-500">Published Posts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                <Eye className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{formatNumber(postsPerformance.summary.total_reach)}</p>
                <p className="text-sm text-slate-500">Total Reach</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-pink-500 flex items-center justify-center">
                <Heart className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{formatNumber(postsPerformance.summary.total_engagement)}</p>
                <p className="text-sm text-slate-500">Total Engagement</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center">
                <Target className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{postsPerformance.summary.avg_engagement_rate}%</p>
                <p className="text-sm text-slate-500">Avg. Engagement Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )}

    {/* Post Performance List */}
    <Card className="border-slate-100">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              Post Performance Breakdown
            </CardTitle>
            <CardDescription>Detailed metrics for each published post</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={postsSortBy} onValueChange={setPostsSortBy}>
              <SelectTrigger className="w-[160px]" data-testid="posts-sort-select">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="engagement">Engagement</SelectItem>
                <SelectItem value="reach">Reach</SelectItem>
                <SelectItem value="clicks">Clicks</SelectItem>
                <SelectItem value="impressions">Impressions</SelectItem>
                <SelectItem value="engagement_rate">Eng. Rate</SelectItem>
                <SelectItem value="published_at">Date</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              onClick={toggleSortOrder}
              data-testid="posts-sort-order-btn"
            >
              {postsSortOrder === 'desc' ? <SortDesc className="h-4 w-4" /> : <SortAsc className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {postsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>
        ) : postsPerformance?.posts?.length > 0 ? (
          <div className="space-y-3">
            {postsPerformance.posts.map((post, i) => (
              <motion.div
                key={post.post_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className={`p-4 rounded-lg border transition-all cursor-pointer ${
                  selectedPost === post.post_id
                    ? 'border-emerald-300 bg-emerald-50'
                    : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50'
                }`}
                onClick={() => fetchPostDetail(post.post_id)}
                data-testid={`post-performance-item-${post.post_id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-sm flex-shrink-0">
                    #{i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-700 line-clamp-2">{post.content}</p>
                    <div className="flex items-center gap-2 mt-2">
                      {post.platforms.map((p) => (
                        <Badge key={p} variant="secondary" className="text-xs px-1.5 py-0">
                          {p}
                        </Badge>
                      ))}
                      <span className="text-xs text-slate-400">
                        {post.published_at ? format(new Date(post.published_at), 'MMM d, yyyy') : 'N/A'}
                      </span>
                      {post.data_source === 'api' && (
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">Live Data</Badge>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4 text-center flex-shrink-0">
                    <div>
                      <p className="text-lg font-bold text-slate-900">{formatNumber(post.metrics.reach)}</p>
                      <p className="text-xs text-slate-500">Reach</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-slate-900">{formatNumber(post.metrics.engagement)}</p>
                      <p className="text-xs text-slate-500">Engagement</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-slate-900">{formatNumber(post.metrics.clicks)}</p>
                      <p className="text-xs text-slate-500">Clicks</p>
                    </div>
                    <div>
                      <Badge
                        className={`${
                          post.metrics.engagement_rate > 5
                            ? 'bg-emerald-100 text-emerald-700'
                            : post.metrics.engagement_rate > 2
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {post.metrics.engagement_rate}%
                      </Badge>
                      <p className="text-xs text-slate-500 mt-1">Eng. Rate</p>
                    </div>
                  </div>
                  <Link to={`/posts/${post.post_id}`} onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="flex-shrink-0">
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-slate-500">
            <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p>No published posts yet</p>
            <Link to="/posts/new">
              <Button className="mt-4 bg-emerald-500 hover:bg-emerald-600">Create Your First Post</Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>

    {/* Post Detail Panel */}
    {selectedPost && (
      <Card className="border-slate-100 border-emerald-200 bg-gradient-to-br from-white to-emerald-50/30">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-emerald-500" />
            Detailed Performance Analysis
          </CardTitle>
          <CardDescription>In-depth metrics and insights for the selected post</CardDescription>
        </CardHeader>
        <CardContent>
          {postDetailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
            </div>
          ) : postDetail ? (
            <div className="space-y-6">
              {/* Metrics Overview */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { icon: Eye, color: 'text-blue-500', value: postDetail.metrics?.reach, label: 'Total Reach', vs: postDetail.comparison?.reach_vs_avg, avg: postDetail.comparison?.avg_reach },
                  { icon: Heart, color: 'text-pink-500', value: postDetail.metrics?.engagement, label: 'Total Engagement', vs: postDetail.comparison?.engagement_vs_avg, avg: postDetail.comparison?.avg_engagement },
                  { icon: MousePointer, color: 'text-emerald-500', value: postDetail.metrics?.clicks, label: 'Total Clicks', vs: postDetail.comparison?.clicks_vs_avg, avg: postDetail.comparison?.avg_clicks },
                ].map(({ icon: Icon, color, value, label, vs, avg }) => (
                  <div key={label} className="p-4 rounded-lg bg-white border border-slate-100">
                    <div className="flex items-center justify-between">
                      <Icon className={`h-5 w-5 ${color}`} />
                      <Badge variant="outline" className={vs > 0 ? 'text-emerald-600 border-emerald-200' : 'text-red-600 border-red-200'}>
                        {vs > 0 ? <ArrowUpRight className="h-3 w-3 mr-1" /> : <ArrowDownRight className="h-3 w-3 mr-1" />}
                        {Math.abs(vs || 0)}%
                      </Badge>
                    </div>
                    <p className="text-2xl font-bold text-slate-900 mt-2">{formatNumber(value)}</p>
                    <p className="text-sm text-slate-500">{label}</p>
                    <p className="text-xs text-slate-400 mt-1">Avg: {formatNumber(avg)}</p>
                  </div>
                ))}
                <div className="p-4 rounded-lg bg-white border border-slate-100">
                  <div className="flex items-center justify-between">
                    <Target className="h-5 w-5 text-amber-500" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900 mt-2">{postDetail.metrics?.engagement_rate}%</p>
                  <p className="text-sm text-slate-500">Engagement Rate</p>
                  <p className="text-xs text-slate-400 mt-1">Click Rate: {postDetail.metrics?.click_rate}%</p>
                </div>
              </div>

              {/* Engagement + Platform Breakdown */}
              <div className="grid lg:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-white border border-slate-100">
                  <h4 className="font-semibold text-slate-900 mb-3">Engagement Breakdown</h4>
                  <div className="space-y-3">
                    {[
                      { icon: ThumbsUp, color: 'text-blue-500', label: 'Likes', value: postDetail.metrics?.likes },
                      { icon: MessageSquare, color: 'text-emerald-500', label: 'Comments', value: postDetail.metrics?.comments },
                      { icon: Share2, color: 'text-purple-500', label: 'Shares', value: postDetail.metrics?.shares },
                    ].map(({ icon: Icon, color, label, value }) => (
                      <div key={label} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className={`h-4 w-4 ${color}`} />
                          <span className="text-sm text-slate-600">{label}</span>
                        </div>
                        <span className="font-semibold">{formatNumber(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-white border border-slate-100">
                  <h4 className="font-semibold text-slate-900 mb-3">Platform Performance</h4>
                  <div className="space-y-3">
                    {postDetail.platform_breakdown &&
                      Object.entries(postDetail.platform_breakdown).map(([platform, data]) => (
                        <div key={platform} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PLATFORM_COLORS[platform] || '#94a3b8' }} />
                            <span className="text-sm text-slate-600 capitalize">{platform}</span>
                            {data.data_source === 'api' && (
                              <Badge className="bg-emerald-100 text-emerald-700 text-[10px] px-1">Live</Badge>
                            )}
                          </div>
                          <div className="text-right">
                            <span className="font-semibold text-sm">{formatNumber(data.engagement)}</span>
                            <span className="text-xs text-slate-400 ml-1">eng</span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>

              {/* AI Insights */}
              {postDetail.insights?.length > 0 && (
                <div className="p-4 rounded-lg bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-100">
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Zap className="h-4 w-4 text-amber-500" />
                    AI Insights
                  </h4>
                  <div className="space-y-2">
                    {postDetail.insights.map((insight) => (
                      <p key={insight.message} className="text-sm text-slate-600 flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                        {insight.message}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Hourly Trend */}
              {postDetail.hourly_trend?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Performance Over Time (First 24 Hours)</h4>
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={postDetail.hourly_trend}>
                        <defs>
                          <linearGradient id="colorTrendReach" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="hour" stroke="#94a3b8" fontSize={11} tickFormatter={(v) => `${v}h`} />
                        <YAxis stroke="#94a3b8" fontSize={11} />
                        <Tooltip
                          contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                          labelFormatter={(v) => `Hour ${v}`}
                        />
                        <Area type="monotone" dataKey="reach" stroke="#10B981" fillOpacity={1} fill="url(#colorTrendReach)" name="Reach" />
                        <Line type="monotone" dataKey="engagement" stroke="#8B5CF6" strokeWidth={2} dot={false} name="Engagement" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <p>Select a post above to view detailed analytics</p>
            </div>
          )}
        </CardContent>
      </Card>
    )}
  </TabsContent>
);

export default PostsTab;
